/**
 * Owner (nursing home) entitlements from `subscription_plans.features` (JSONB) plus free-tier defaults.
 *
 * DB helpers: `get_subscription_limits(user_uuid)` reads max_nursing_homes & max_media_uploads;
 * `has_subscription_feature(user_uuid, feature_name)` checks boolean string "true" in JSON.
 *
 * ┌────────────────────────────┬──────────────────────────────────────────────────────────┐
 * │ JSON key                   │ Where to gate / product intent                           │
 * ├────────────────────────────┼──────────────────────────────────────────────────────────┤
 * │ lead_management            │ LeadsPage, Overview “view lead details” (full PII list)   │
 * │ max_nursing_homes          │ Claim / add-home flows; get_subscription_limits RPC       │
 * │ max_media_uploads          │ MediaPage gallery uploads (images in nursing_home_media)│
 * │ media_uploads              │ If false, block new uploads entirely (rare)             │
 * │ advanced_analytics         │ Future: owner analytics dashboard beyond basics         │
 * │ basic_analytics            │ Future: simple profile stats / impressions              │
 * │ profile_management         │ Future: lock profile edits (keep true on all real plans) │
 * │ priority_support           │ Marketing / support SLA only                            │
 * │ api_access                 │ Enterprise integrations                                   │
 * │ review_management          │ Future: respond to reviews, request reviews             │
 * │ featured_placement         │ Future: boosted ranking / badges                        │
 * │ sms_lead_alerts            │ Future: Twilio etc.                                       │
 * └────────────────────────────┴──────────────────────────────────────────────────────────┘
 */

export interface OwnerPlanFeatures {
  profile_management: boolean;
  media_uploads: boolean;
  basic_analytics: boolean;
  email_support: boolean;
  max_nursing_homes: number;
  max_media_uploads: number;
  lead_management: boolean;
  advanced_analytics: boolean;
  priority_support: boolean;
  api_access: boolean;
  /** Request/respond tools — add to plan JSON when shipped */
  review_management: boolean;
  featured_placement: boolean;
  sms_lead_alerts: boolean;
}

function asBool(v: unknown): boolean {
  return v === true || v === "true";
}

function asInt(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.floor(v));
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseInt(v, 10);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return fallback;
}

/** Unpaid owners: presence tier (matches use-subscription fallbacks when no active sub row). */
export const FREE_OWNER_PLAN_FEATURES: OwnerPlanFeatures = {
  profile_management: true,
  media_uploads: true,
  basic_analytics: true,
  email_support: false,
  max_nursing_homes: 1,
  max_media_uploads: 20,
  lead_management: false,
  advanced_analytics: false,
  priority_support: false,
  api_access: false,
  review_management: false,
  featured_placement: false,
  sms_lead_alerts: false,
};

/** Normalise a row from `subscription_plans.features` (or API). */
export function normalizeOwnerPlanFeaturesJson(
  raw: Record<string, unknown> | null | undefined,
): OwnerPlanFeatures {
  if (!raw) {
    return { ...FREE_OWNER_PLAN_FEATURES };
  }
  return {
    profile_management: asBool(raw.profile_management ?? true),
    media_uploads: asBool(raw.media_uploads ?? true),
    basic_analytics: asBool(raw.basic_analytics ?? false),
    email_support: asBool(raw.email_support ?? false),
    max_nursing_homes: asInt(raw.max_nursing_homes, FREE_OWNER_PLAN_FEATURES.max_nursing_homes),
    max_media_uploads: asInt(raw.max_media_uploads, FREE_OWNER_PLAN_FEATURES.max_media_uploads),
    lead_management: asBool(raw.lead_management),
    advanced_analytics: asBool(raw.advanced_analytics),
    priority_support: asBool(raw.priority_support),
    api_access: asBool(raw.api_access),
    review_management: asBool(raw.review_management),
    featured_placement: asBool(raw.featured_placement),
    sms_lead_alerts: asBool(raw.sms_lead_alerts),
  };
}

/**
 * Effective entitlements for UI gating.
 * When the user has an active or trialling subscription, use the plan’s JSON; otherwise free tier.
 */
export function resolveOwnerPlanFeatures(
  subscriptionActive: boolean,
  planFeatures: Record<string, unknown> | null | undefined,
): OwnerPlanFeatures {
  if (!subscriptionActive) {
    return { ...FREE_OWNER_PLAN_FEATURES };
  }
  return normalizeOwnerPlanFeaturesJson(planFeatures ?? null);
}

/** Bullets for /owner/subscription plan cards — keep in sync with seeded JSON keys. */
export function marketingBulletsFromPlanFeatures(f: OwnerPlanFeatures): string[] {
  return [
    f.profile_management && "Profile management",
    f.media_uploads && `Up to ${f.max_media_uploads} gallery photos`,
    f.basic_analytics && "Basic analytics",
    f.advanced_analytics && "Advanced analytics",
    f.lead_management && "Full lead details & management",
    f.review_management && "Review management tools",
    f.featured_placement && "Featured placement in search",
    f.sms_lead_alerts && "SMS lead alerts",
    f.priority_support && "Priority support",
    f.api_access && "API access",
  ].filter(Boolean) as string[];
}
